import WholesaleNotice from '@/features/products/components/WholesaleNotice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCart } from '@/features/cart/context/CartContext';
import { apiClient } from '@/lib/apiClient';
import { ordersApi } from '@/lib/apiServices';
import { customerApi } from '@/features/customers/api/customerApi';
import { Building2, CheckCircle, Gift } from 'lucide-react';
import { resolveUnitPrice } from '@/lib/pricing';
import { fmt } from '@/lib/format';
import {
  getPersianErrorMessage,
  persianDisplayText,
} from '@/lib/persianUi';
import { PAYMENT_MODE_OPTIONS, PAYMENT_MESSAGES } from '../paymentCopy';
import { getCheckoutPayableAmount } from '../lib/checkoutAmount';
import {
  navigateToPaymentRedirect,
  paymentErrorMessage,
  paymentOptionUnavailableMessage,
} from '../payment';

const getQuoteErrorMessage = (error) => {
  return getPersianErrorMessage(error, 'خطا در محاسبه هزینهٔ ارسال.');
};

function PaymentOptionSelector({ options, selectedOption, onSelect }) {
  const visibleOptions = options.filter(
    (option) => option.reason !== 'OPTION_DISABLED',
  );
  const showSelector = visibleOptions.length > 1
    || visibleOptions.some((option) => !option.available);
  if (!showSelector) return null;

  return (
    <fieldset className="payment-methods">
      <legend>{PAYMENT_MESSAGES.methodHeading}</legend>
      {visibleOptions.map((option) => {
        const method = PAYMENT_MODE_OPTIONS.find(
          (item) => item.value === option.option,
        );
        if (!option.available) {
          return (
            <div className="payment-method payment-method--unavailable" key={option.option}>
              <strong>{method?.label ?? option.option}</strong>
              <span>{paymentOptionUnavailableMessage(option.reason)}</span>
            </div>
          );
        }

        return (
          <label className="payment-method" key={option.option}>
            <input
              checked={selectedOption?.option === option.option}
              name="payment-mode"
              onChange={() => onSelect(option.option)}
              type="radio"
              value={option.option}
            />
            <span className="payment-method-copy">
              <strong>{method?.label ?? option.option}</strong>
              {method?.description && <span>{method.description}</span>}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

export default function CheckoutPage() {
  const { cart, cartWarning, refreshCart } = useCart();
  const { orderId: routeOrderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isPaymentRoute = Boolean(routeOrderId);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [paymentOrderLoading, setPaymentOrderLoading] = useState(isPaymentRoute);
  const [paymentOrderState, setPaymentOrderState] = useState('loading');
  const [loadedPaymentOrderId, setLoadedPaymentOrderId] = useState(null);
  const [basketEmpty, setBasketEmpty] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [locationRequired, setLocationRequired] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [selections, setSelections] = useState({});
  const [appliedCompanyCode, setAppliedCompanyCode] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('auto_company_info') || '{}').company_code || '';
    } catch {
      return '';
    }
  });
  const [appliedEmployeeId, setAppliedEmployeeId] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('auto_company_info') || '{}').identification_value || '';
    } catch {
      return '';
    }
  });
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState('idle');
  const [referralMessage, setReferralMessage] = useState('');
  const companyInfo = useMemo(() => (
    appliedCompanyCode && appliedEmployeeId
      ? {
        company_code: appliedCompanyCode,
        identification_value: appliedEmployeeId,
      }
      : undefined
  ), [appliedCompanyCode, appliedEmployeeId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      sessionStorage.removeItem('auto_company_info');
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const cartKey = useMemo(
    () => JSON.stringify(cart.map((item) => [item.id, item.quantity || 1])),
    [cart],
  );
  const selectionsKey = useMemo(
    () => JSON.stringify(selections),
    [selections],
  );

  const [step, setStep] = useState(
    /** @type {'cart'|'payment'|'done'} */ ('cart')
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(
    /** @type {string|null} */ (null)
  );

  const [orderId, setOrderId] = useState(
    /** @type {string|null} */ (null)
  );
  const [orderSummary, setOrderSummary] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentMode, setPaymentMode] = useState(
    location.state?.paymentMode ?? null,
  );
  const [pendingModeMismatch, setPendingModeMismatch] = useState(false);
  const [orderPaymentOptions, setOrderPaymentOptions] = useState(null);
  const [chequePrepayment, setChequePrepayment] = useState('');
  const [chequeAmounts, setChequeAmounts] = useState(['']);

  const [quote, setQuote] = useState(null);
  const quoteRequestId = useRef(0);
  const activeOrderId = routeOrderId ?? orderId;
  const paymentOptions = isPaymentRoute
    ? orderPaymentOptions ?? location.state?.paymentOptions ?? []
    : quote?.payment_options ?? [];
  const visiblePaymentOptions = paymentOptions.filter(
    (option) => !['OPTION_DISABLED', 'PAYMENT_NOT_REQUIRED'].includes(option.reason),
  );
  const availablePaymentOptions = visiblePaymentOptions.filter(
    (option) => option.available,
  );
  const showPaymentOptions = visiblePaymentOptions.length > 1
    || visiblePaymentOptions.some((option) => !option.available);
  const selectedPaymentOption = availablePaymentOptions.find(
    (option) => option.option === paymentMode,
  ) ?? (availablePaymentOptions.length === 1 ? availablePaymentOptions[0] : null);
  const payableAmount = getCheckoutPayableAmount({
    paymentDetails,
    orderSummary,
    quote,
    paymentMode: selectedPaymentOption?.option,
    chequePrepaymentAmount: chequePrepayment,
  });
  const noAvailablePaymentOptions = paymentOptions.length > 0
    && visiblePaymentOptions.length > 0
    && availablePaymentOptions.length === 0;
  const paymentNotRequired = isPaymentRoute
    ? location.state?.paymentRequired === false
    : quote?.payment_required === false;
  const paymentContextAvailable = !isPaymentRoute
    || paymentNotRequired
    || (
      Array.isArray(orderPaymentOptions ?? location.state?.paymentOptions)
      && (
        Boolean(selectedPaymentOption)
        || availablePaymentOptions.length > 1
        || noAvailablePaymentOptions
      )
    );
  const shippingGroups = quote?.shipping_groups ?? [];
  const shippingCost = quote?.shipping_cost;
  const shippingSelectionInvalid = shippingGroups.some((group) => {
    const chosenProvider = selections[group.group_key]
      ?? group.selected_provider
      ?? group.options?.find((option) => option.available)?.provider;
    return !group.options?.some(
      (option) => option.provider === chosenProvider && option.available,
    );
  });

  const refreshQuote = useCallback(async (profile, providerSelections, compInfo) => {
    const requestId = ++quoteRequestId.current;
    try {
      const { data, error: requestError, response } = await apiClient.POST(
        '/api/v1/customer/cart/checkout/quote',
        {
          body: {
            address: profile?.address ?? '',
            postal_code: profile?.postal_code ?? '',
            selections: providerSelections,
            company_info: compInfo,
          },
        },
      );

      if (requestError || !data) {
        const detail = requestError?.detail;
        const code = requestError?.code ?? detail?.code;
        const isBasketEmpty = response?.status === 400
          && (requestError?.code === 'CART_EMPTY'
            || detail === 'Basket is empty');
        const isLocationRequired = response?.status === 422
          && code === 'CUSTOMER_LOCATION_REQUIRED';
        if (requestId === quoteRequestId.current) {
          if (isBasketEmpty) {
            const refreshedCart = await refreshCart();
            if (requestId !== quoteRequestId.current) return null;
            setBasketEmpty(
              !Array.isArray(refreshedCart) || refreshedCart.length === 0,
            );
          } else {
            setBasketEmpty(false);
          }
          setLocationRequired(isLocationRequired);
          setQuoteError(getQuoteErrorMessage(requestError));
          setQuote(null);
        }
        return null;
      }

      if (requestId === quoteRequestId.current) {
        setLocationRequired(false);
        setQuoteError('');
        setBasketEmpty(false);
        setQuote(data);
      }
      return data;
    } catch (requestError) {
      if (requestId === quoteRequestId.current) {
        setLocationRequired(false);
        setQuoteError(getQuoteErrorMessage(requestError));
        setQuote(null);
      }
      return null;
    }
  }, [refreshCart]);

  useEffect(() => {
    if (isPaymentRoute) return undefined;
    let cancelled = false;
    refreshCart()
      .then((items) => {
        if (!cancelled) {
          setCartLoaded(true);
          setBasketEmpty(Array.isArray(items) && items.length === 0);
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          console.error('Failed to refresh checkout basket:', requestError);
          setCartLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isPaymentRoute, refreshCart]);

  const loadCustomerProfile = useCallback(async () => {
    try {
      const profile = await customerApi.getProfile();
      setCustomerProfile(profile);
      setProfileLoaded(true);
    } catch (requestError) {
      console.error(requestError);
      setQuoteError('خطا در دریافت اطلاعات پروفایل');
      setProfileLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isPaymentRoute || step !== 'cart' || orderId) return undefined;
    Promise.resolve().then(loadCustomerProfile);
    const refreshOnFocus = () => loadCustomerProfile();
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [isPaymentRoute, loadCustomerProfile, orderId, step]);

  useEffect(() => {
    if (
      isPaymentRoute
      || step !== 'cart'
      || orderId
      || !cartLoaded
      || !profileLoaded
      || !customerProfile
      || cart.length === 0
    ) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => {
        if (cancelled) return null;
        setQuoteLoading(true);
        setQuote(null);
        return refreshQuote(customerProfile, selections, companyInfo);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    cartKey,
    cart.length,
    isPaymentRoute,
    orderId,
    step,
    customerProfile,
    customerProfile?.address,
    customerProfile?.postal_code,
    customerProfile?.location_id,
    customerProfile?.latitude,
    customerProfile?.longitude,
    profileLoaded,
    cartLoaded,
    refreshQuote,
    selections,
    selectionsKey,
    companyInfo,
  ]);

  useEffect(() => {
    if (!routeOrderId) return undefined;
    let cancelled = false;

    ordersApi.get(routeOrderId)
      .then(async ({ data }) => {
        if (cancelled) return;
        setLoadedPaymentOrderId(routeOrderId);
        setOrderId(data.id);
        setOrderSummary(data);

        if (data.is_paid) {
          navigate(`/payment/result?order_id=${encodeURIComponent(data.id)}`, {
            replace: true,
          });
          return;
        }

        const expired = data.expires_at
          && new Date(data.expires_at).getTime() <= Date.now();
        if (data.payment_status === 'prepaid_awaiting_cheque_confirmation') {
          setPaymentOrderState('cheque_pending');
        } else if (data.payment_status !== 'unpaid') {
          setPaymentOrderState('closed');
        } else if (expired) {
          setPaymentOrderState('expired');
        } else if (!data.is_open || data.delivery_status !== 'pending') {
          setPaymentOrderState('closed');
        } else {
          const {
            data: options,
            error: optionsError,
            response: optionsResponse,
          } = await apiClient.GET(
            '/api/v1/customer/orders/{order_id}/payment-options',
            { params: { path: { order_id: routeOrderId } } },
          );
          if (cancelled) return;
          if (optionsError || !options) {
            setError(
              optionsResponse?.status === 409
                ? PAYMENT_MESSAGES.orderPaymentUnavailable
                : PAYMENT_MESSAGES.paymentOptionsError,
            );
            setPaymentOrderState('options_error');
          } else {
            setOrderPaymentOptions(options);
            const available = options.filter(
              (option) => option.available
                && !['OPTION_DISABLED', 'PAYMENT_NOT_REQUIRED'].includes(option.reason),
            );
            const requestedMode = location.state?.paymentMode;
            const matchingMode = available.find(
              (option) => option.option === requestedMode,
            );
            setPaymentMode(matchingMode?.option
              ?? (available.length === 1 ? available[0].option : null));
            setPaymentOrderState('ready');
            setStep('payment');
          }
        }
        setPaymentOrderLoading(false);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setLoadedPaymentOrderId(routeOrderId);
        const detail = requestError?.detail;
        setError(
          ['ORDER_NOT_FOUND', 'NOT_AUTHORIZED'].includes(requestError?.code)
            || detail === 'Order not found'
            || detail === 'Not authorized'
            ? 'سفارش پیدا نشد یا دسترسی به آن امکان‌پذیر نیست.'
            : getQuoteErrorMessage(requestError),
        );
        setPaymentOrderState(
          ['ORDER_NOT_FOUND', 'NOT_AUTHORIZED'].includes(requestError?.code)
            || detail === 'Order not found'
            || detail === 'Not authorized'
            ? 'not_found'
            : 'error',
        );
        setPaymentOrderLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location.state?.paymentMode, navigate, routeOrderId]);

  const handleApplyReferral = async () => {
    if (!referralCode.trim()) return;
    setReferralStatus('loading');
    setReferralMessage('');

    try {
      const { error: requestError } = await apiClient.POST(
        '/api/v1/customer/referral/apply',
        { body: { referral_code: referralCode.trim() } },
      );
      if (requestError) {
        throw new Error(getPersianErrorMessage(requestError, 'کد معرف نامعتبر است.'));
      }
      setReferralStatus('success');
      setReferralMessage('کد معرف با موفقیت ثبت شد.');
      await loadCustomerProfile();
    } catch (requestError) {
      setReferralStatus('error');
      setReferralMessage(
        requestError?.message || 'ثبت کد معرف با خطا مواجه شد.',
      );
    }
  };

  // Step 1: Create order
  const handleCreateOrder = async () => {
    if (activeOrderId) return;
    setLoading(true);
    setError(null);

    try {
      const checkoutQuote = quote ?? await refreshQuote(customerProfile, selections, companyInfo);
      if (!checkoutQuote) {
        throw new Error(quoteError || 'خطا در محاسبه هزینه ارسال و پرداخت');
      }
      setQuote(checkoutQuote);

      const {
        data: order,
        error: orderErr,
        response,
      } = await apiClient.POST('/api/v1/customer/cart/checkout', {
        body: {
          address: customerProfile?.address ?? '',
          postal_code: customerProfile?.postal_code ?? '',
          selections,
          expected_shipping_cost: checkoutQuote.shipping_cost,
          company_info: companyInfo,
          referral_code: referralCode.trim() || null,
        },
      });

      if (orderErr || !order) {
        if (response?.status === 409) {
          await refreshQuote(customerProfile, selections, companyInfo);
          throw new Error('هزینه ارسال تغییر کرده است. گزینه‌های ارسال را بررسی و دوباره تأیید کنید.');
        }

        let detail = 'خطا در ایجاد سفارش';

        if (response?.status === 401) {
          detail =
            'لطفاً وارد حساب کاربری خود شوید';
        } else if (
          response?.status === 422
          && (orderErr?.code === 'CUSTOMER_LOCATION_REQUIRED'
            || orderErr?.detail?.code === 'CUSTOMER_LOCATION_REQUIRED'
            || orderErr?.detail === 'CUSTOMER_LOCATION_REQUIRED')
        ) {
          setLocationRequired(true);
          setQuoteError('برای تکمیل سفارش، نشانی، کد پستی، شهر و موقعیت خود را در پروفایل ثبت کنید.');
          detail = 'برای تکمیل سفارش، نشانی، کد پستی، شهر و موقعیت خود را در پروفایل ثبت کنید.';
        } else {
          console.error(orderErr);
        }

        throw new Error(detail);
      }

      setOrderId(order.id);
      setOrderSummary(order);
      setStep('payment');
      await refreshCart();
      navigate(`/checkout/payment/${encodeURIComponent(order.id)}`, {
        replace: true,
        state: {
          paymentMode: selectedPaymentOption?.option
            ?? (checkoutQuote.payment_required ? null : 'normal'),
          paymentOptions: checkoutQuote.payment_options ?? [],
          paymentRequired: checkoutQuote.payment_required,
        },
      });
    } catch (err) {
      setError(
        err?.message || 'خطا در ارتباط'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Payment
  const handlePay = async () => {
    if (!activeOrderId) return;
    if (isPaymentRoute && !paymentContextAvailable) return;
    if (
      selectedPaymentOption?.option === 'cheque'
      && (!chequePrepayment || chequeAmounts.length === 0 || chequeAmounts.some((amount) => !amount))
    ) {
      setError(PAYMENT_MESSAGES.chequeTermsRequired);
      return;
    }
    setLoading(true);
    setError(null);
    setPendingModeMismatch(false);
    const requestedPaymentMode = paymentNotRequired
      ? 'normal'
      : selectedPaymentOption?.option;
    if (!requestedPaymentMode) {
      setLoading(false);
      setError(PAYMENT_MESSAGES.paymentContextUnavailable);
      return;
    }

    try {
      const {
        data: payment,
        error: payErr,
        response,
      } = await apiClient.POST(
        '/api/v1/finance/initiate',
        {
          body: {
            order_id: activeOrderId,
            method: 'debit',
            payment_mode: requestedPaymentMode,
            ...(requestedPaymentMode === 'cheque' && {
              cheque_prepayment_amount: chequePrepayment,
              cheque_amounts: chequeAmounts,
            }),
          },
        }
      );

      if (payErr || !payment) {
        const code = payErr?.code ?? payErr?.detail?.code;
        if (response?.status === 409 && code === 'PAYMENT_OPTION_UNAVAILABLE') {
          const refreshedQuote = await refreshQuote(customerProfile, selections);
          const refreshedReason = refreshedQuote?.payment_options?.find(
            (option) => option.option === requestedPaymentMode,
          )?.reason;
          setError(paymentOptionUnavailableMessage(
            refreshedReason ?? payErr?.detail?.reason,
          ));
          return;
        }
        if (code === 'PENDING_PAYMENT_MODE_MISMATCH') {
          setPendingModeMismatch(true);
        }
        if (requestedPaymentMode === 'cheque') {
          if (code === 'CHEQUE_COUNT_OUT_OF_RANGE') {
            throw new Error(PAYMENT_MESSAGES.chequeCountError
              .replace('{min}', String(payErr.detail.min))
              .replace('{max}', String(payErr.detail.max)));
          }
          if (code === 'CHEQUE_PREPAYMENT_TOO_LOW') {
            throw new Error(PAYMENT_MESSAGES.chequePrepaymentError
              .replace('{minimum}', String(payErr.detail.minimum)));
          }
          const chequeErrors = {
            CHEQUE_AMOUNTS_MUST_BE_WHOLE_UNITS: PAYMENT_MESSAGES.chequeWholeUnitsError,
            CHEQUE_AMOUNT_MUST_BE_POSITIVE: PAYMENT_MESSAGES.chequePositiveAmountError,
            CHEQUE_TOTAL_MISMATCH: PAYMENT_MESSAGES.chequePlanMismatchError,
            CHEQUE_TERMS_REQUIRED: PAYMENT_MESSAGES.chequeTermsRequired,
          };
          if (chequeErrors[code]) throw new Error(chequeErrors[code]);
        }
        throw new Error(paymentErrorMessage(payErr, response?.status));
      }

      setPaymentDetails(payment);
      if (requestedPaymentMode === 'cheque') {
        try {
          window.sessionStorage.setItem(
            `payment-cheque-plan:${activeOrderId}`,
            JSON.stringify({ prepayment: chequePrepayment, amounts: chequeAmounts }),
          );
        } catch (storageError) {
          console.error('Unable to retain cheque registration amounts:', storageError);
        }
      }
      if (!payment.payment_required) {
        navigate(`/payment/result?order_id=${encodeURIComponent(activeOrderId)}`, {
          replace: true,
        });
        return;
      }
      navigateToPaymentRedirect(payment.redirect);
    } catch (err) {
      setError(
        err?.message || PAYMENT_MESSAGES.genericError
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isPaymentRoute && cart.length === 0 && step === 'cart') {
    return (
      <div
        className="container checkout-page"
        style={{
          paddingTop: '2rem',
          textAlign: 'center',
        }}
      >
        <p>سبد خرید خالی است.</p>
      </div>
    );
  }

  if (isPaymentRoute) {
    if (
      paymentOrderLoading
      || loadedPaymentOrderId !== routeOrderId
      || paymentOrderState === 'loading'
    ) {
      return (
        <main className="container checkout-page" style={{ paddingTop: '2rem', maxWidth: '700px', margin: '0 auto' }}>
          <div className="card" role="status" style={{ padding: '2rem', textAlign: 'center' }}>
            در حال دریافت سفارش...
          </div>
        </main>
      );
    }

    if (paymentOrderState === 'not_found' || paymentOrderState === 'error') {
      return (
        <main className="container checkout-page" style={{ paddingTop: '2rem', maxWidth: '700px', margin: '0 auto' }}>
          <div className="card" role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{error || 'دریافت اطلاعات سفارش ممکن نشد.'}</p>
            <Link className="btn btn-primary" to="/profile/orders">مشاهده سفارش‌ها</Link>
          </div>
        </main>
      );
    }

    if (
      paymentOrderState === 'expired'
      || paymentOrderState === 'closed'
      || paymentOrderState === 'cheque_pending'
    ) {
      return (
        <main className="container checkout-page" style={{ paddingTop: '2rem', maxWidth: '700px', margin: '0 auto' }}>
          <div className="card" role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>
              {paymentOrderState === 'cheque_pending'
                ? PAYMENT_MESSAGES.prepaidAwaitingCheque
                : paymentOrderState === 'expired'
                ? 'مهلت پرداخت این سفارش به پایان رسیده است.'
                : 'این سفارش دیگر برای پرداخت باز نیست.'}
            </p>
            <Link
              className="btn btn-primary"
              to={paymentOrderState === 'cheque_pending'
                ? `/profile/orders/${encodeURIComponent(activeOrderId)}`
                : '/cart'}
            >
              {paymentOrderState === 'cheque_pending' ? PAYMENT_MESSAGES.viewPendingOrder : PAYMENT_MESSAGES.backToCart}
            </Link>
          </div>
        </main>
      );
    }

    if (paymentOrderState === 'options_error') {
      return (
        <main className="container checkout-page" style={{ paddingTop: '2rem', maxWidth: '700px', margin: '0 auto' }}>
          <div className="card" role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{error || PAYMENT_MESSAGES.paymentOptionsError}</p>
            <Link className="btn btn-primary" to={`/profile/orders/${encodeURIComponent(activeOrderId)}`}>
              {PAYMENT_MESSAGES.viewPendingOrder}
            </Link>
          </div>
        </main>
      );
    }

    if (!paymentContextAvailable) {
      return (
        <main className="container checkout-page" style={{ paddingTop: '2rem', maxWidth: '700px', margin: '0 auto' }}>
          <div className="card" role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
            <p>{PAYMENT_MESSAGES.paymentContextUnavailable}</p>
            <Link className="btn btn-primary" to={`/profile/orders/${encodeURIComponent(activeOrderId)}`}>
              {PAYMENT_MESSAGES.viewPendingOrder}
            </Link>
          </div>
        </main>
      );
    }

    const items = orderSummary?.items ?? [];
    return (
      <main className="container checkout-page" style={{ paddingTop: '2rem', maxWidth: '700px', margin: '0 auto' }}>
        <h2 style={{ marginBottom: '1.5rem' }}>{PAYMENT_MESSAGES.payOrder}</h2>
        {error && <div role="alert" className="orders-state orders-state--error">{error}</div>}
        <section className="card checkout-summary-card" style={{ padding: '1.25rem', marginBottom: '1.25rem' }}>
          <h3>{PAYMENT_MESSAGES.paymentSummary}</h3>
          {items.map((item) => (
            <div className="checkout-summary-row" key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0' }}>
              <span>{persianDisplayText(item.product?.name_fa, 'محصول')} × {fmt(item.quantity)}</span>
              <span>{fmt(Number(item.unit_price) * item.quantity)} تومان</span>
            </div>
          ))}
          <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.75rem 0' }} />
          <div className="checkout-summary-total" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{PAYMENT_MESSAGES.productAmount}</span>
            <span>{fmt(orderSummary?.product_total_amount)} تومان</span>
          </div>
          <div className="checkout-summary-total" style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem' }}>
            <span>{PAYMENT_MESSAGES.shippingCost}</span>
            <span>{fmt(orderSummary?.shipping_cost)} تومان</span>
          </div>
          <div className="checkout-summary-total" style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, paddingTop: '0.75rem' }}>
            <span>{PAYMENT_MESSAGES.payableAmount}</span>
            <span>
              {fmt(payableAmount)} تومان
            </span>
          </div>
        </section>
        {showPaymentOptions && (
          <section className="card checkout-payment-options" aria-label={PAYMENT_MESSAGES.paymentOptionsAria}>
            <PaymentOptionSelector
              onSelect={(value) => {
                setPaymentMode(value);
                setError(null);
                setPendingModeMismatch(false);
              }}
              options={paymentOptions}
              selectedOption={selectedPaymentOption}
            />
          </section>
        )}
        {selectedPaymentOption?.option === 'cheque' && (
          <section className="card checkout-payment-options" aria-label={PAYMENT_MESSAGES.chequePrepaymentAria}>
            <label>
              {PAYMENT_MESSAGES.chequePrepaymentLabel}
              <input
                inputMode="numeric"
                min="1"
                onChange={(event) => setChequePrepayment(event.target.value)}
                required
                step="1"
                type="number"
                value={chequePrepayment}
              />
            </label>
            <label>
              {PAYMENT_MESSAGES.chequeCountLabel}
              <input
                inputMode="numeric"
                min="0"
                onChange={(event) => {
                  const count = Number.parseInt(event.target.value, 10);
                  setChequeAmounts((current) => {
                    if (!Number.isInteger(count) || count < 0) return current;
                    return Array.from({ length: count }, (_, index) => current[index] ?? '');
                  });
                }}
                step="1"
                type="number"
                value={chequeAmounts.length}
              />
            </label>
            {chequeAmounts.map((amount, index) => (
              <label key={`cheque-amount-${index}`}>
                {PAYMENT_MESSAGES.chequeAmountLabel} {fmt(index + 1)}
                <input
                  inputMode="numeric"
                  min="1"
                  onChange={(event) => setChequeAmounts((current) => (
                    current.map((value, amountIndex) => (
                      amountIndex === index ? event.target.value : value
                    ))
                  ))}
                  required
                  step="1"
                  type="number"
                  value={amount}
                />
                <button
                  className="btn btn-outline"
                  onClick={() => setChequeAmounts((current) => current.filter((_, amountIndex) => amountIndex !== index))}
                  type="button"
                >
                  {PAYMENT_MESSAGES.removeCheque}
                </button>
              </label>
            ))}
            <button
              className="btn btn-outline"
              onClick={() => setChequeAmounts((current) => [...current, ''])}
              type="button"
            >
              {PAYMENT_MESSAGES.addCheque}
            </button>
            <p className="order-payment-status">{PAYMENT_MESSAGES.chequeTermsRequired}</p>
          </section>
        )}
        {noAvailablePaymentOptions && (
          <p className="order-payment-status" role="status">
            {PAYMENT_MESSAGES.noAvailablePaymentOption}
          </p>
        )}
        {pendingModeMismatch && (
          <div className="payment-pending-actions">
            <Link
              className="payment-pending-order-link"
              to={`/profile/orders/${encodeURIComponent(activeOrderId)}`}
            >
              {PAYMENT_MESSAGES.viewPendingOrder}
            </Link>
            <Link className="payment-pending-order-link" to="/profile/orders">
              {PAYMENT_MESSAGES.waitForPendingPayment}
            </Link>
          </div>
        )}

        <button
          onClick={handlePay}
          disabled={
            loading
            || paymentOrderLoading
            || noAvailablePaymentOptions
            || (!paymentNotRequired && !selectedPaymentOption)
          }
          className="btn btn-primary"
          style={{ width: '100%', padding: '0.85rem', fontSize: '1rem' }}
        >
          {loading ? PAYMENT_MESSAGES.preparingPayment : PAYMENT_MESSAGES.continuePayment}
        </button>
      </main>
    );
  }

  return (
    <div
      className="container checkout-page"
      style={{
        paddingTop: '2rem',
        maxWidth: '700px',
        margin: '0 auto',
      }}
    >
      <h2 style={{ marginBottom: '1.5rem' }}>
        پرداخت
      </h2>

      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #ef4444',
            borderRadius: '10px',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            color: '#991b1b',
            fontSize: '0.85rem',
          }}
        >
          <i
            className="fas fa-exclamation-circle"
            style={{ marginLeft: '0.4rem' }}
          />
          {error}
        </div>
      )}
      {quoteError && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '10px',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            color: '#92400e',
            fontSize: '0.9rem',
          }}
        >
          {quoteError}
          {locationRequired && (
            <>
              {' '}
              <Link
                to="/profile"
                state={{ from: '/checkout' }}
                style={{ fontWeight: 700, textDecoration: 'underline' }}
              >
                ثبت شهر و موقعیت در پروفایل
              </Link>
            </>
          )}
        </div>
      )}

      {cartWarning && (
        <div
          role="alert"
          style={{
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '10px',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            color: '#92400e',
            fontSize: '0.9rem',
          }}
        >
          {cartWarning}
        </div>
      )}

      {!cartLoaded ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          در حال دریافت سبد خرید...
        </div>
      ) : basketEmpty ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <p>سبد خرید شما خالی است. کالاهای سبد در حساب شما پیدا نشدند.</p>
          <Link className="btn btn-primary" to="/products">بازگشت به فروشگاه</Link>
        </div>
      ) : step === 'done' ? (
        <div
          className="card"
          style={{
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <i
            className="fas fa-check-circle"
            style={{
              fontSize: '3rem',
              color: '#10b981',
              display: 'block',
              marginBottom: '1rem',
            }}
          />

          <h3 style={{ marginBottom: '0.5rem' }}>
            پرداخت با موفقیت انجام شد!
          </h3>

          <p
            style={{
              color: '#6b7280',
              marginBottom: '1.5rem',
            }}
          >
            سفارش شما ثبت شد و در حال پردازش است.
          </p>
          {orderId && <Link className="btn btn-primary" to={`/profile/orders/${orderId}`}>مشاهده سفارش</Link>}
        </div>
      ) : step === 'payment' ? (
        <>
          <div
            className="card checkout-summary-card"
            style={{
              padding: '1.25rem',
              marginBottom: '1.25rem',
            }}
          >
            <h3 style={{ marginBottom: '0.75rem' }}>
              خلاصه سفارش
            </h3>
            {(orderSummary?.items ?? cart).map((item) => (
              <div
                className="checkout-summary-row"
                key={item.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.3rem 0',
                  fontSize: '0.9rem',
                }}
              >
                <span>
                  {persianDisplayText(item.product?.name_fa ?? item.name_fa, 'محصول')} × {fmt(item.quantity || 1)}
                </span>
                <span style={{ fontWeight: 500 }}>
                  {fmt(
                    item.unit_price != null
                      ? Number(item.unit_price) * (item.quantity || 1)
                      : resolveUnitPrice(item, item.quantity || 1) * (item.quantity || 1),
                  )} تومان
                </span>
              </div>
            ))}
            <hr
              style={{
                border: 'none',
                borderTop:
                  '1px solid #e5e7eb',
                margin: '0.75rem 0',
              }}
            />

            <div
              className="checkout-summary-total"
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              <span>مبلغ محصولات</span>
              <span>{fmt(orderSummary?.product_total_amount ?? quote?.product_total)} تومان</span>
            </div>
            <div className="checkout-summary-total" style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem' }}>
              <span>هزینه ارسال</span>
              <span>{fmt(orderSummary?.shipping_cost ?? shippingCost)} تومان</span>
            </div>
            <div className="checkout-summary-total" style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem' }}>
              <span>کالابرگ اعمال‌شده</span>
              <span>{fmt(paymentDetails?.kalabarg_applied ?? orderSummary?.kala_barg_amount ?? quote?.kalabarg_applied ?? 0)} تومان</span>
            </div>
            <div className="checkout-summary-total" style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, paddingTop: '0.75rem' }}>
              <span>مبلغ قابل پرداخت</span>
              <span>
                {fmt(payableAmount)} تومان
              </span>
            </div>
          </div>

          {showPaymentOptions && (
            <section className="card checkout-payment-options" aria-label={PAYMENT_MESSAGES.paymentOptionsAria}>
              <PaymentOptionSelector
                onSelect={(value) => {
                  setPaymentMode(value);
                  setError(null);
                  setPendingModeMismatch(false);
                }}
                options={paymentOptions}
                selectedOption={selectedPaymentOption}
              />
            </section>
          )}
          {noAvailablePaymentOptions && (
            <p className="order-payment-status" role="status">
              {PAYMENT_MESSAGES.noAvailablePaymentOption}
            </p>
          )}

          <button
            onClick={handlePay}
            disabled={loading || noAvailablePaymentOptions}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '0.85rem',
              fontSize: '1rem',
            }}
          >
            {loading ? (
              <>
                <i
                  className="fas fa-spinner fa-pulse"
                  style={{
                    marginLeft: '0.4rem',
                  }}
                />
                در حال پردازش...
              </>
            ) : (
              'پرداخت'
            )}
          </button>
        </>
      ) : (
        <>
          <div
            className="card checkout-summary-card"
            style={{
              padding: '1.25rem',
              marginBottom: '1.25rem',
            }}
          >
            <h3 style={{ marginBottom: '0.75rem' }}>
              بررسی سبد خرید
            </h3>

            {cart.map((item) => (
              <div key={item.id} style={{ padding: '0.3rem 0' }}>
                <div
                  className="checkout-summary-row"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.9rem',
                  }}
                >
                  <span>
                    {item.name_fa} × {item.quantity || 1}
                  </span>
                  <span style={{ fontWeight: 500 }}>
                    {fmt(resolveUnitPrice(item, item.quantity || 1) * (item.quantity || 1))} تومان
                  </span>
                </div>
                <WholesaleNotice item={item} quantity={item.quantity || 1} />
              </div>
            ))}
            
            <hr
              style={{
                border: 'none',
                borderTop:
                  '1px solid #e5e7eb',
                margin: '0.75rem 0',
              }}
            />

            <div
              className="checkout-summary-row"
              style={{
                display: 'flex',
                justifyContent:
                  'space-between',
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              <span>مجموع</span>

              <span
                style={{ color: '#6366f1' }}
              >
                {fmt(quote?.product_total)} تومان
              </span>
            </div>
          </div>

          <section className="card checkout-summary-card" style={{ padding: '1.25rem', marginBottom: '1.25rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Gift size={20} color="#f59e0b" />
              کد معرف دارید؟
            </h3>
            {referralStatus === 'success' ? (
              <p role="status" style={{ color: '#166534' }}>
                <CheckCircle size={18} /> {referralMessage}
              </p>
            ) : (
              <div className="checkout-inline-field" style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(event) => setReferralCode(event.target.value)}
                  placeholder="کد معرف"
                  aria-label="کد معرف"
                />
                <button
                  type="button"
                  onClick={handleApplyReferral}
                  disabled={referralStatus === 'loading' || !referralCode.trim()}
                >
                  {referralStatus === 'loading' ? 'در حال ثبت...' : 'ثبت'}
                </button>
              </div>
            )}
            {referralStatus === 'error' && <p role="alert">{referralMessage}</p>}
          </section>

          {(companyInfo || quote?.company) && (
            <section className="card checkout-summary-card" style={{ padding: '1.25rem', marginBottom: '1.25rem' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Building2 size={20} color="#6366f1" />
                تخفیف یا اعتبار شرکت
              </h3>
              {quote?.company ? (
                <p role="status">
                  قرارداد «{quote.company.company_name}» در محاسبه سفارش اعمال شد.
                </p>
              ) : (
                <p>در حال بررسی اعتبار قرارداد شرکت...</p>
              )}
              {quote?.company?.warning && <p role="alert">{quote.company.warning}</p>}
              <button
                type="button"
                onClick={() => {
                  setAppliedCompanyCode('');
                  setAppliedEmployeeId('');
                }}
              >
                لغو استفاده از قرارداد شرکت
              </button>
            </section>
          )}

          <section
            className="card"
            style={{ padding: '1.25rem', marginBottom: '1.25rem' }}
            aria-labelledby="shipping-options-title"
          >
            <h3 id="shipping-options-title" style={{ marginBottom: '0.75rem' }}>
              روش و هزینه ارسال
            </h3>
            {quoteLoading ? (
              <p>در حال محاسبه گزینه‌های ارسال...</p>
            ) : shippingGroups.length > 0 ? (
              <>
                {shippingGroups.map((group) => {
                  const options = group.options ?? [];
                  const selectedProvider = selections[group.group_key]
                    ?? group.selected_provider
                    ?? options.find((option) => option.available)?.provider;
                  return (
                    <div
                      key={group.group_key}
                      style={{
                        borderTop: '1px solid #e5e7eb',
                        padding: '0.85rem 0',
                      }}
                    >
                      <strong>{persianDisplayText(group.origin_name, 'محل ارسال')}</strong>
                      <div style={{ marginTop: '0.5rem' }}>
                        {options.length === 0 && <p>گزینه‌ای برای این محل ارسال موجود نیست.</p>}
                        {options.length > 0 && !options.some((option) => option.available) && (
                          <p style={{ color: '#991b1b' }}>در حال حاضر روش ارسالی برای این محل موجود نیست.</p>
                        )}
                        {options.map((option) => {
                          const providerName = option.provider === 'sohapeyk'
                            ? 'ارسال با پیک (سهاپیک)'
                            : option.provider === 'flat_rate'
                              ? 'ارسال استاندارد'
                              : 'روش ارسال';
                          return (
                            <label
                              className="checkout-shipping-option"
                              key={`${option.provider}:${option.service_code || ''}`}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.5rem',
                                padding: '0.35rem 0',
                                color: option.available ? 'inherit' : '#6b7280',
                              }}
                            >
                              <input
                                type="radio"
                                name={`shipping-${group.group_key}`}
                                value={option.provider}
                                checked={selectedProvider === option.provider}
                                disabled={!option.available}
                                onChange={() => setSelections((current) => ({
                                  ...current,
                                  [group.group_key]: option.provider,
                                }))}
                              />
                              <span>
                                <strong>{providerName}</strong>
                                {option.available ? (
                                  <>
                                    {' — '}
                                    {fmt(option.price)} تومان
                                    {option.eta_minutes != null
                                      && ` · حدود ${fmt(option.eta_minutes)} دقیقه`}
                                  </>
                                ) : (
                                  ` — ${persianDisplayText(option.unavailable_reason, 'در دسترس نیست')}`
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  <span>مجموع هزینه ارسال</span>
                  <span>{fmt(shippingCost)} تومان</span>
                </div>
              </>
            ) : quote ? (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>هزینه ارسال</span>
                <span>{fmt(shippingCost)} تومان</span>
              </div>
            ) : (
              <p>برای مشاهده هزینه ارسال، اطلاعات موقعیت خود را کامل کنید.</p>
            )}
          </section>

          <button
            onClick={handleCreateOrder}
            disabled={loading || quoteLoading || !quote || shippingSelectionInvalid}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '0.85rem',
              fontSize: '1rem',
            }}
          >
            {loading ? (
              <>
                <i
                  className="fas fa-spinner fa-pulse"
                  style={{
                    marginLeft: '0.4rem',
                  }}
                />
                در حال ایجاد سفارش...
              </>
            ) : (
              'ادامه به صفحه پرداخت'
            )}
          </button>
        </>
      )}
    </div>
  );
}